const ACTIVITY_EVENTS = Object.freeze([
  "pointermove",
  "pointerdown",
  "touchstart",
  "wheel",
  "scroll",
  "keydown",
]);

export function createDemoController({
  configs,
  inactivityMs,
  hotspotDurationMs,
  onIdle,
  onStart,
  onStop,
  onActivate,
  targetDocument = document,
  targetWindow = targetDocument.defaultView ?? globalThis,
}) {
  let started = false;
  let destroyed = false;
  let ready = false;
  let startPending = false;
  let active = false;
  let currentIndex = 0;
  let inactivityTimer = null;
  let hotspotTimer = null;

  function clearInactivityTimer() {
    if (inactivityTimer === null) return;
    targetWindow.clearTimeout(inactivityTimer);
    inactivityTimer = null;
  }

  function clearHotspotTimer() {
    if (hotspotTimer === null) return;
    targetWindow.clearTimeout(hotspotTimer);
    hotspotTimer = null;
  }

  function scheduleInactivityTimer() {
    clearInactivityTimer();
    inactivityTimer = targetWindow.setTimeout(handleIdle, inactivityMs);
  }

  function activateCurrentHotspot() {
    if (!active || configs.length === 0) return;

    onActivate(configs[currentIndex].key);
    clearHotspotTimer();
    hotspotTimer = targetWindow.setTimeout(() => {
      currentIndex = (currentIndex + 1) % configs.length;
      activateCurrentHotspot();
    }, hotspotDurationMs);
  }

  function startDemoIfPossible() {
    if (
      destroyed ||
      !started ||
      !ready ||
      !startPending ||
      active ||
      configs.length === 0
    ) {
      return;
    }

    startPending = false;
    active = true;
    currentIndex = 0;
    onStart();
    activateCurrentHotspot();
  }

  function handleIdle() {
    inactivityTimer = null;
    startPending = true;
    onIdle();
    startDemoIfPossible();
  }

  function handleActivity() {
    if (destroyed || !started) return;

    startPending = false;
    const wasActive = active;
    active = false;
    clearHotspotTimer();
    if (wasActive) onStop();
    scheduleInactivityTimer();
  }

  return {
    start() {
      if (started || destroyed) return;
      started = true;

      ACTIVITY_EVENTS.forEach((eventName) => {
        targetDocument.addEventListener(eventName, handleActivity, {
          capture: true,
          passive: true,
        });
      });
      scheduleInactivityTimer();
    },
    notifyReady() {
      if (destroyed) return;
      ready = true;
      startDemoIfPossible();
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      startPending = false;
      const wasActive = active;
      active = false;
      clearInactivityTimer();
      clearHotspotTimer();
      if (wasActive) onStop();

      ACTIVITY_EVENTS.forEach((eventName) => {
        targetDocument.removeEventListener(eventName, handleActivity, {
          capture: true,
        });
      });
    },
  };
}
